import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TeamsPendingCard } from "@/components/TeamReleaseGate";
import {
  BookOpen, Trophy, NotebookPen, Library, ArrowUpRight, ClipboardList, Building2,
  Users, ClipboardCheck, AlertCircle, Calendar, GraduationCap, Lock, ListTodo, FileText, Download,
} from "lucide-react";
import EmailPreferences from "@/components/EmailPreferences";

function formatLongDate(input) {
  if (!input) return "";
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const n = d.getDate();
  const ord = (n>=11&&n<=13)?"th":(n%10===1?"st":n%10===2?"nd":n%10===3?"rd":"th");
  return `${days[d.getDay()]} ${n}${ord} ${months[d.getMonth()]} ${d.getFullYear()}`;
}
function isPastIso(iso){ if(!iso) return false; const dt=new Date(iso.endsWith("Z")||iso.includes("+")?iso:iso+"Z"); return !Number.isNaN(dt.getTime())&&dt.getTime()<Date.now(); }

export default function Dashboard() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [awaitingRelease, setAwaitingRelease] = useState(false);
  const [allTeams, setAllTeams] = useState([]);
  const [programme, setProgramme] = useState({});
  const [days, setDays] = useState([]);
  const [upcomingSlots, setUpcomingSlots] = useState([]);
  const [mySponsor, setMySponsor] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, t, p, s, tAll] = await Promise.all([
          api.get("/days").then((r) => r.data).catch(() => []),
          api.get("/teams/my").then((r) => r.data).catch(() => ({ team: null })),
          api.get("/programme").then((r) => r.data).catch(() => ({})),
          api.get("/my-sponsor").then((r) => r.data).catch(() => null),
          api.get("/teams").then((r) => r.data).catch(() => []),
        ]);
        setDays(d || []);
        setTeam(t.team);
        setAwaitingRelease(!!t.awaiting_release);
        setProgramme(p || {});
        setMySponsor(s);
        setAllTeams(tAll || []);
        if (t?.team?.id) {
          try {
            const all = (await api.get("/mentor-slots")).data || [];
            const mine = all
              .filter((slot) => slot.booked_by_team_id === t.team.id)
              .sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || ""));
            setUpcomingSlots(mine.filter((slot) => !isPastIso(slot.slot_end)).slice(0, 3));
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const isMentor = user?.role === "mentor";
  const isAdmin = user?.role === "admin" || user?.role === "faculty";
  const isStudent = user?.role === "student";

  const openingRequired = isStudent && !user?.opening_survey_completed;
  const openingReminder = false;  // superseded by full tripwire above
  const exitReminder = !isMentor && programme.exit_survey_url && !user?.exit_survey_completed;

  const nextSession = (days || []).find((d) => {
    const iso = d.date && String(d.date).length >= 10 ? d.date.slice(0,10) : null;
    if (!iso) return false;
    return iso >= new Date().toISOString().slice(0, 10);
  });

  // -------- TRIPWIRE: entry survey blocks everything else --------
  if (openingRequired) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="dashboard-entry-tripwire">
        <div className="max-w-xl w-full border-2 border-[#25AAAA] bg-white p-8 md:p-10">
          <div className="w-14 h-14 bg-[#25AAAA] text-white flex items-center justify-center">
            <Lock size={26} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#25AAAA] mt-6">One step to unlock iTeams</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-[#0F1F2E] mt-2 leading-tight">
            Complete the entry survey to get started.
          </h1>
          <p className="mt-4 text-sm text-[#525252] leading-relaxed">
            It takes about 5 minutes. This baseline helps your mentors and admin
            support you well through the seven weeks. As soon as you&apos;re done, your
            dashboard, sessions and team space unlock.
          </p>
          {programme.opening_survey_url ? (
            <Link to="/survey/opening" data-testid="dashboard-entry-tripwire-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#25AAAA] hover:bg-[#1e7d7d] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wide">
              <ClipboardCheck size={16} /> Open the entry survey
              <ArrowUpRight size={16} />
            </Link>
          ) : (
            <div className="mt-6 border border-[#f4b847] bg-[#FEF3D0] px-4 py-3 text-sm text-[#7A5B17]" data-testid="dashboard-entry-tripwire-cta">
              Your admin hasn&#39;t published the entry survey link yet. Check back shortly.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {(openingReminder || exitReminder) && (
        <div className="space-y-3" data-testid="dashboard-survey-reminders">
          {openingReminder && (
            <Link to="/survey/opening" className="block border-2 border-[#A23232] bg-[#FDEDED] p-5 hover:bg-[#F8D7D5] transition-colors" data-testid="dashboard-opening-survey-reminder">
              <div className="flex items-center gap-3">
                <AlertCircle size={22} className="text-[#A23232]" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#A23232]">Action required · 5 min</div>
                  <div className="font-display font-bold text-base md:text-lg tracking-tight mt-0.5 text-[#7A1F1F]">Please complete the entry survey.</div>
                </div>
                <ArrowUpRight size={18} className="text-[#A23232]" />
              </div>
            </Link>
          )}
          {exitReminder && (
            <Link to="/survey/exit" className="block border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-4 hover:bg-[#FBE7A8] transition-colors" data-testid="dashboard-exit-survey-reminder">
              <div className="flex items-center gap-3">
                <ClipboardCheck size={18} className="text-[#7A5B17]" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#7A5B17]">Before the final pitch</div>
                  <div className="font-display font-bold text-sm md:text-base tracking-tight mt-0.5 text-[#7A5B17]">Please complete the exit survey.</div>
                  <div className="text-[11px] text-[#7A5B17] mt-1">Required if you want James to write you a LinkedIn recommendation for your iTeams work.</div>
                </div>
                <ArrowUpRight size={16} className="text-[#7A5B17]" />
              </div>
            </Link>
          )}
        </div>
      )}

      <LinkedInOptInCard user={user} />

      {/* Pitch winners banner */}
      {(programme.pitch_prizes || []).some((p) => p.team_id) && (
        <PitchWinnersBanner prizes={programme.pitch_prizes} allTeams={allTeams} />
      )}

      {/* Hero */}
      <div className="border border-[#0F1F2E] bg-white p-8 md:p-10 relative overflow-hidden">
        <div className="absolute right-[-40px] top-[-40px] opacity-[0.04] pointer-events-none"
             style={{ width: 320, height: 320,
               backgroundImage: "repeating-linear-gradient(45deg, #0F1F2E 0 1px, transparent 1px 16px)" }} />
        <span className="inline-block w-16 h-1 bg-[#F4B847]" />
        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#25AAAA] mt-4">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-2 leading-none text-[#0F1F2E]">
          Eight sessions over seven weeks. A real business.
          <br />
          <span className="text-[#25AAAA]">Solve their challenge.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-[#525252] leading-relaxed">
          Work in a small team on a real sponsor&apos;s challenge, backed by mentors and guest speakers.
          Your final pitch goes back to the business itself.
        </p>
        {isStudent && (
          <a href="/guide/teams.pdf" target="_blank" rel="noopener noreferrer"
             className="mt-5 inline-flex items-center gap-2 bg-[#0F1F2E] text-[#F4B847] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1a2f42] transition-colors"
             data-testid="dashboard-team-guide-btn">
            <Download size={14} /> Download the Team Guide (PDF)
          </a>
        )}
      </div>

      {/* Team status */}
      {isStudent && (
        team ? (
          <div className="border border-[#25AAAA] bg-[#E6F7F7] p-5 flex items-center justify-between flex-wrap gap-3" data-testid="dashboard-team-banner">
            <div className="flex items-center gap-4">
              <Users size={20} className="text-[#25AAAA]" strokeWidth={1.5} />
              <div>
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA]">Team</div>
                <div className="font-display text-lg font-bold tracking-tight text-[#0F1F2E]">
                  {team.name}{mySponsor?.name ? ` · working with ${mySponsor.name}` : ""}
                </div>
              </div>
            </div>
            <Link to="/my-team" className="text-sm text-[#25AAAA] font-semibold uppercase tracking-wider hover:underline" data-testid="dashboard-team-link">Open team workspace →</Link>
          </div>
        ) : awaitingRelease ? (
          <TeamsPendingCard compact />
        ) : (
          <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-5 flex items-center justify-between flex-wrap gap-3" data-testid="dashboard-no-team-banner">
            <div className="flex items-center gap-4">
              <Users size={20} className="text-[#7A5B17]" />
              <div>
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#7A5B17]">Action required</div>
                  <div className="font-display text-lg font-bold tracking-tight text-[#7A5B17]">Join your team to get started</div>
              </div>
            </div>
            <Link to="/team-setup" className="text-sm font-semibold uppercase tracking-wider border border-[#0F1F2E] px-4 py-2 hover:bg-[#0F1F2E] hover:text-white" data-testid="dashboard-join-team-link">Enter join code</Link>
          </div>
        )
      )}

      {/* Next session */}
      {nextSession && (
        <div className="border border-[#E5E7EB] bg-white p-5" data-testid="dashboard-next-session">
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Next session</div>
          <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-2xl font-black tracking-tight text-[#0F1F2E]">{nextSession.title}</div>
              <div className="text-sm text-[#525252] inline-flex items-center gap-1.5 mt-1">
                <Calendar size={14} /> {formatLongDate(nextSession.date)}
                {nextSession.room ? ` · ${nextSession.room}` : ""}
              </div>
              {nextSession.objective && <p className="text-sm text-[#525252] mt-2 max-w-2xl">{nextSession.objective}</p>}
            </div>
            <Link to="/workbook" className="text-sm text-[#25AAAA] font-semibold uppercase tracking-wider hover:underline">
              Open sessions →
            </Link>
          </div>
        </div>
      )}

      {/* Upcoming mentor sessions */}
      {isStudent && team && upcomingSlots.length > 0 && (
        <div data-testid="dashboard-upcoming-mentor-sessions">
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] flex items-center gap-2 mb-3"><GraduationCap size={12} /> Upcoming mentor sessions</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {upcomingSlots.map((s) => (
              <div key={s.id} className="border border-[#25AAAA] bg-[#E6F7F7] p-4" data-testid={`dashboard-upcoming-slot-${s.id}`}>
                <div className="font-display text-base font-bold tracking-tight truncate text-[#0F1F2E]">{s.mentor_name}</div>
                <div className="text-xs text-[#1e7d7d] mt-1">
                  {formatLongDate(s.day_date)} · {s.slot_start?.slice(11,16)}–{s.slot_end?.slice(11,16)}
                </div>
                {(s.meeting_link || s.mentor_default_meeting_link) && (
                  <a href={s.meeting_link || s.mentor_default_meeting_link} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-block bg-[#25AAAA] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#1e7d7d]">
                    Join meeting
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div>
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-4">Jump in</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickCard to="/workbook" label="Sessions" icon={BookOpen} description="All 8 sessions — brief, resources, tasks."/>
          <QuickCard to="/my-team" label="My team" icon={Users} description="Your team, mentor, business and shared notes."/>
          <QuickCard to="/my-sponsor" label="My sponsor" icon={Building2} description="The business you're working with."/>
          <QuickCard to="/tasks" label="Team tasks" icon={ListTodo} description="Split work up. Track what's done."/>
        </div>
      </div>

      {isAdmin && (
        <div>
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-4">For admin</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <QuickCard to="/admin/sponsors" label="Sponsors" icon={Building2} description="Manage the businesses."/>
            <QuickCard to="/teams" label="Teams" icon={Users} description="Team setup and members."/>
            <QuickCard to="/admin/mentor-reports" label="Mentor reports" icon={ClipboardList} description="See what mentors are seeing."/>
            <QuickCard to="/admin/final-pitches" label="Final pitches" icon={Trophy} description="Track deck submissions."/>
          </div>
        </div>
      )}

      <EmailPreferences />
    </div>
  );
}

function QuickCard({ to, label, description, icon: Icon }) {
  return (
    <Link to={to} data-testid={`quick-${to.replace(/^\//, "").replace(/\//g, "-")}`}
      className="group block border border-[#E5E7EB] bg-white text-[#0F1F2E] hover:border-[#0F1F2E] p-5 transition-all">
      <div className="flex items-start justify-between">
        <Icon size={18} strokeWidth={1.5} />
        <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="font-display font-semibold tracking-tight mt-6 text-lg">{label}</div>
      <div className="text-xs mt-2 text-[#525252]">{description}</div>
    </Link>
  );
}

function LinkedInOptInCard({ user }) {
  const [state, setState] = React.useState({
    linkedin_url: user?.linkedin_url || "",
    wants_linkedin_recommendation: !!user?.wants_linkedin_recommendation,
    essex_in_experience: !!user?.essex_in_experience,
  });
  const [saving, setSaving] = React.useState(false);
  if (!user || user.role !== "student") return null;
  const exitDone = !!user.exit_survey_completed;
  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", patch);
      setState({
        linkedin_url: data.linkedin_url || "",
        wants_linkedin_recommendation: !!data.wants_linkedin_recommendation,
        essex_in_experience: !!data.essex_in_experience,
      });
    } catch { /* toast handled elsewhere */ }
    finally { setSaving(false); }
  };
  const allSet = exitDone && state.wants_linkedin_recommendation
                 && state.linkedin_url && state.essex_in_experience;
  return (
    <div className="border border-[#0F1F2E] bg-white p-5 md:p-6" data-testid="dashboard-linkedin-optin">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#0077B5]">LinkedIn recommendation</div>
          <div className="font-display font-black text-lg md:text-xl tracking-tight mt-1 text-[#0F1F2E]">
            Want James to write you a LinkedIn recommendation for your iTeams work?
          </div>
          <p className="text-xs text-[#525252] mt-2 leading-relaxed">
            Tick the box, add your LinkedIn URL and confirm Essex is in your Experience.
            You&apos;ll also need the exit survey completed. James will write one for every
            student who ticks all four before the end of the programme.
          </p>
        </div>
        {allSet && (
          <span className="text-[10px] uppercase tracking-wider bg-[#0A8C5C] text-white px-2 py-1 font-bold">All set</span>
        )}
      </div>
      <div className="mt-4 space-y-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.wants_linkedin_recommendation}
            onChange={(e) => save({ wants_linkedin_recommendation: e.target.checked })}
            disabled={saving}
            data-testid="linkedin-optin-wants"
            className="mt-1"
          />
          <span>Yes, I&apos;d like a LinkedIn recommendation from James.</span>
        </label>
        {state.wants_linkedin_recommendation && (
          <>
            <div className="ml-6">
              <label className="block text-xs text-[#525252] mb-1">Your LinkedIn profile URL</label>
              <input
                type="url"
                value={state.linkedin_url}
                onChange={(e) => setState((s) => ({ ...s, linkedin_url: e.target.value }))}
                onBlur={(e) => save({ linkedin_url: e.target.value.trim() })}
                placeholder="https://www.linkedin.com/in/yourhandle/"
                data-testid="linkedin-optin-url"
                className="w-full max-w-md border border-[#E5E7EB] px-3 py-2 text-sm h-10"
              />
            </div>
            <label className="flex items-start gap-2 text-sm ml-6">
              <input
                type="checkbox"
                checked={state.essex_in_experience}
                onChange={(e) => save({ essex_in_experience: e.target.checked })}
                disabled={saving}
                data-testid="linkedin-optin-essex"
                className="mt-1"
              />
              <span>I&apos;ve added <strong>University of Essex</strong> to my Experience section on LinkedIn.</span>
            </label>
            <div className={`ml-6 text-xs mt-1 ${exitDone ? "text-[#0A8C5C]" : "text-[#A23232]"}`}>
              {exitDone
                ? "✓ Exit survey completed."
                : "✗ Please complete the exit survey when it opens — required for James to send the recommendation."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PitchWinnersBanner({ prizes, allTeams }) {
  const teamById = React.useMemo(() => {
    const m = {};
    (allTeams || []).forEach((t) => { m[t.id] = t; });
    return m;
  }, [allTeams]);
  const rankLabel = (r) => (r === 1 ? "1st" : r === 2 ? "2nd" : r === 3 ? "3rd" : `${r}th`);
  const ordered = (prizes || []).filter((p) => p.team_id).sort((a, b) => a.rank - b.rank);
  if (ordered.length === 0) return null;
  return (
    <div className="border-2 border-[#f4b847] bg-gradient-to-br from-[#FEF3D0] to-[#FEF9E7] p-6" data-testid="dashboard-pitch-winners">
      <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#7A5B17] flex items-center gap-1.5">
        <Trophy size={12} /> Final pitch winners
      </div>
      <div className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-2 text-[#0F1F2E]">
        Congratulations!
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {ordered.map((p) => {
          const t = teamById[p.team_id];
          return (
            <div key={p.rank} className="bg-white border border-[#f4b847] p-4" data-testid={`dashboard-winner-rank-${p.rank}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#7A5B17]">
                {p.label || `${rankLabel(p.rank)} place`}
              </div>
              <div className="font-display text-xl font-black tracking-tight mt-1 text-[#0F1F2E]">
                {t?.name || "Team"}
              </div>
              <div className="text-sm text-[#525252] mt-1">
                £{(p.amount || 0).toLocaleString()} · split between team members
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
